// components/Cms/AiUsagePanel.js
// ──────────────────────────────────────────────────────────────────────────────
// Shows the institute's CMS AI quota usage — used inside CmsManagement
// "AI Usage" tab. Displays:
//   • Plan + quota progress bar
//   • Per-feature breakdown (page copy, blogs, course marketing, syllabus)
//   • Recent generations table (paginated)
// ──────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { Sparkles, RefreshCw, AlertTriangle, FileText, BookOpen, GraduationCap, Layout } from 'lucide-react';
import {
  AppCard, AppCardContent, AppButton, StatsCard, StatsGrid, StatusBadge,
  AppDataTable, FilterToolbar,
} from '../ui';
import { getCmsAiUsage } from '../../utils/cmsAdminApi';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); ai-usage-panel.scss deleted with
// it. The quota warning is the find:
//
//     border: 1px solid var(--color-warning, rgba(234, 179, 8, 0.4));
//
// `--color-warning` DOES exist (a Phase 1 alias resolving to `hsl(var(--warning))`),
// so the fallback never ran and the border painted a fully saturated amber where
// a 40 % wash was intended — a fallback that reads as the design and is dead.
// The three bar-fill tones had the same shape and are the status tokens now, so
// they finally match the pills beside them in dark mode.
//
// The `&__stats` / `&__bar-card` / `&__breakdown-card` classes carried nothing
// but `margin: 0`, which StatsGrid and AppCard already set; the props that
// passed them are gone rather than translated.
const STATE = 'tw-p-6 tw-text-center tw-text-content-muted';

const WARNING =
  'tw-flex tw-items-start tw-gap-2 tw-rounded-sm tw-border tw-border-warning/40 ' +
  'tw-bg-warning/[0.12] tw-px-[0.85rem] tw-py-[0.65rem] tw-text-[0.85rem] tw-text-warning-fg';

const BAR_FILL = 'tw-h-full tw-rounded-full tw-transition-[width] tw-duration-300';

const SECTION_TITLE = 'tw-mb-[0.65rem] tw-mt-0 tw-text-[0.95rem] tw-text-content';
const FEATURE_META = {
  cms_page_content:    { label: 'Page Content',     icon: Layout },
  cms_blog:            { label: 'Blog Posts',       icon: FileText },
  cms_course_marketing:{ label: 'Course Marketing', icon: BookOpen },
  cms_syllabus:        { label: 'Syllabus Drafts',  icon: GraduationCap },
};

const RECENT_LIMIT = 10;

const formatNum = (n) => (n == null ? '—' : Number(n).toLocaleString());
// ⚠️ IST explicitly. The box is UTC and a bare toLocaleString names the previous
// day for anything generated after 05:30 — this column is a timestamp log.
const formatDate = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—';

const DASH = <span className="tw-text-content-subtle">—</span>;

/**
 * The Recent Generations columns.
 *
 * Static, so they live outside the component — nothing here closes over state.
 * No `sortable` and no `filter`: the rows are one server page (see the table).
 */
const RECENT_COLUMNS = [
  {
    key: 'createdAt',
    label: 'When',
    width: 170,
    render: (r) => <span className="tw-whitespace-nowrap">{formatDate(r.createdAt)}</span>,
  },
  {
    key: 'feature',
    label: 'Feature',
    render: (r) => {
      const key = r.feature || r._id;
      return (FEATURE_META[key] || {}).label || key || 'Unknown';
    },
  },
  {
    key: 'contextLabel',
    label: 'Context',
    className: 'tw-max-w-[240px] tw-truncate tw-text-content-muted',
    render: (r) => r.contextLabel || DASH,
  },
  {
    key: 'tokensUsed',
    label: 'Tokens',
    align: 'right',
    render: (r) => <span className="tw-tabular-nums">{formatNum(r.tokensUsed)}</span>,
  },
  {
    key: 'status',
    label: 'Status',
    render: () => <StatusBadge status="success">Success</StatusBadge>,
  },
];

const AiUsagePanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentPage, setRecentPage] = useState(1);
  const [recentLoading, setRecentLoading] = useState(false);

  const load = useCallback(async (page = 1, isPageChange = false) => {
    if (isPageChange) {
      setRecentLoading(true);
    } else {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await getCmsAiUsage(page, RECENT_LIMIT);
      const payload = res?.data || res;
      setData((prev) => isPageChange
        ? { ...prev, recent: payload.recent, recentTotal: payload.recentTotal, recentPage: payload.recentPage }
        : payload,
      );
    } catch (err) {
      if (!isPageChange) setError(err?.response?.data?.message || err?.message || 'Failed to load usage');
    } finally {
      if (isPageChange) setRecentLoading(false);
      else setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, false); }, [load]);

  const handlePageChange = (p) => {
    setRecentPage(p);
    load(p, true);
  };

  if (loading) return <div className={STATE}>Loading AI usage…</div>;
  if (error) return (
    <div className={`${STATE} tw-inline-flex tw-items-center tw-justify-center tw-gap-[0.4rem] tw-text-danger-fg`}>
      <AlertTriangle size={16} /> {error}
    </div>
  );
  if (!data) return null;

  const { enabled, reason, used = 0, limit = 0, remaining, resetAt, planName, breakdown = [], recent = [], recentTotal = 0 } = data;
  const totalPages = Math.ceil(recentTotal / RECENT_LIMIT);
  const isUnlimited = limit === -1;
  const pct = isUnlimited || !limit ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const pctLabel = isUnlimited ? 'Unlimited' : `${pct}%`;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* Header */}
      <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
        <div>
          <h4 className="tw-m-0 tw-inline-flex tw-items-center tw-gap-[0.4rem] tw-text-[1.05rem] tw-text-content">
            <Sparkles size={16} /> AI Content Generation
          </h4>
          <p className="tw-mb-0 tw-mt-[0.2rem] tw-text-[0.8rem] tw-text-content-muted">
            {planName ? `Plan: ${planName}` : 'Subscription plan'} •
            Resets on {formatDate(resetAt)}
          </p>
        </div>
        <AppButton variant="secondary" onClick={() => { setRecentPage(1); load(1, false); }}>
          <RefreshCw size={14} /> Refresh
        </AppButton>
      </div>

      {/* Status banner */}
      {!enabled && (
        <div className={WARNING}>
          <AlertTriangle size={16} />
          <span>
            AI content generation is currently <strong>disabled</strong>
            {reason ? ` (${reason})` : ''}. Contact your platform administrator
            or upgrade your plan to enable it.
          </span>
        </div>
      )}

      {/* Stats */}
      <StatsGrid cols={3}>
        <StatsCard
          icon={Sparkles}
          title="Tokens Used (this cycle)"
          value={formatNum(used)}
        />
        <StatsCard
          icon={Layout}
          title="Token Limit"
          value={isUnlimited ? 'Unlimited' : formatNum(limit)}
        />
        <StatsCard
          icon={RefreshCw}
          title="Remaining"
          value={isUnlimited ? '∞' : formatNum(remaining)}
        />
      </StatsGrid>

      {/* Progress bar */}
      {!isUnlimited && limit > 0 && (
        <AppCard>
          <AppCardContent>
            <div className="tw-mb-[0.4rem] tw-flex tw-justify-between tw-text-[0.85rem] tw-text-content-muted">
              <span>Monthly Quota</span>
              <span className="tw-font-semibold tw-text-content">{pctLabel}</span>
            </div>
            <div className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-surface-sunken">
              <div
                className={`${BAR_FILL} ${pct > 90 ? 'tw-bg-danger' : pct > 70 ? 'tw-bg-warning' : 'tw-bg-success'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <small className="tw-mt-2 tw-block tw-text-xs tw-text-content-subtle">
              {formatNum(used)} of {formatNum(limit)} tokens used.
              Quota resets {formatDate(resetAt)}.
            </small>
          </AppCardContent>
        </AppCard>
      )}

      {/* Breakdown by feature */}
      <AppCard>
        <AppCardContent>
          <h5 className={SECTION_TITLE}>Usage by Feature</h5>
          {breakdown.length === 0 ? (
            <p className="tw-m-0 tw-text-[0.85rem] tw-text-content-muted">No AI generations yet this cycle.</p>
          ) : (
            <div className="tw-flex tw-flex-col tw-gap-[0.55rem]">
              {breakdown.map((b) => {
                const featureKey = b.feature || b._id;
                const meta = FEATURE_META[featureKey] || { label: featureKey || 'Unknown', icon: Sparkles };
                const Icon = meta.icon;
                return (
                  <div key={featureKey} className="tw-flex tw-items-center tw-justify-between tw-rounded-sm tw-border tw-border-line tw-bg-surface-alt tw-px-[0.7rem] tw-py-[0.55rem]">
                    <div className="tw-inline-flex tw-items-center tw-gap-2 tw-text-[0.88rem] tw-text-content">
                      <Icon size={14} /> <span>{meta.label}</span>
                    </div>
                    <div className="tw-inline-flex tw-gap-4 tw-text-[0.8rem] tw-text-content-muted">
                      <span>{formatNum(b.count)} call{b.count === 1 ? '' : 's'}</span>
                      <span>{formatNum(b.tokens)} tokens</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* Recent generations.
          ⚠️ SERVER-paginated (`load(page, true)`), so this gets the table's
          chrome and the server's paginator and NO column filters or sort — one
          page of 20 filtered in the browser would say nothing about the rest.
          `AppDataTable` is a card in its own right, so there is no `AppCard`
          around it; the heading is its `toolbar`. */}
      <AppDataTable
        fill={false}
        toolbar={
          <FilterToolbar flush>
            <span className="tw-text-[0.95rem] tw-text-content">Recent Generations</span>
            {recentTotal > 0 && (
              <span className="tw-rounded-full tw-border tw-border-line tw-bg-surface-alt tw-px-[0.6rem] tw-py-[0.15rem] tw-text-xs tw-text-content-muted">
                {formatNum(recentTotal)} total
              </span>
            )}
          </FilterToolbar>
        }
        columns={RECENT_COLUMNS}
        rows={recent}
        rowKey={(r) => r._id || `${r.createdAt}-${r.feature}`}
        loading={recentLoading}
        emptyMessage="No recent activity."
        pagination={totalPages > 1 ? {
          page: recentPage,
          totalPages,
          total: recentTotal,
          pageSize: RECENT_LIMIT,
          // ⚠️ `page` and `onChange`. This call site passed `currentPage` and
          // `onPageChange`, which Paginator does not accept — so no page was
          // ever marked current and clicking one did nothing at all.
          onChange: handlePageChange,
          infoLabel: 'generations',
        } : undefined}
      />
    </div>
  );
};

export default AiUsagePanel;
